import type { TourRouteLocation } from '@/components/ui/TourRouteMap';

export interface TourMapDayInput {
    id: string;
    dayNumber?: number;
    title?: string | null;
    location_title?: string | null;
    location_description?: string | null;
    description?: string | null;
    location_address?: string | null;
    lat?: number | null;
    lng?: number | null;
    images?: Array<string | { url?: string } | null>;
    stops?: Array<{
        lat?: number | null;
        lng?: number | null;
        location_address?: string | null;
        location_title?: string | null;
        note?: string | null;
    } | null> | null;
}

function resolveDayImage(day: TourMapDayInput): string | undefined {
    const dayImage = day.images?.[0];
    if (!dayImage) return undefined;
    return typeof dayImage === 'string' ? dayImage : dayImage.url;
}

function hasCoords(lat: unknown, lng: unknown, addr: unknown): boolean {
    const numericLat = Number(lat);
    const numericLng = Number(lng);
    const hasLatLng =
        lat !== null && lat !== undefined && lat !== '' &&
        lng !== null && lng !== undefined && lng !== '' &&
        Number.isFinite(numericLat) && Number.isFinite(numericLng) &&
        Math.abs(numericLat) <= 90 && Math.abs(numericLng) <= 180;
    const hasAddr = addr != null && String(addr).trim().length > 0;
    return hasLatLng || hasAddr;
}

/** Boş string/null atlanır; itinerary'deki gün metni öncelikli kullanılabilir. */
export function firstNonEmpty(...candidates: (string | null | undefined)[]): string | undefined {
    for (const value of candidates) {
        if (value != null && String(value).trim() !== '') {
            return value;
        }
    }
    return undefined;
}

/** Tur programından harita + story için ortak durak listesi üretir. */
export function buildTourMapLocations(mapDays: TourMapDayInput[]): TourRouteLocation[] {
    type Loc = TourRouteLocation & {
        day_id: string;
        day_number?: number;
        order_index: number;
        day_stop_index?: number;
        day_stop_total?: number;
        image?: string;
    };

    const flat: Loc[] = [];
    let stopOrder = 0;

    for (const day of mapDays) {
        const imageUrl = resolveDayImage(day);
        const stops = Array.isArray(day.stops) ? day.stops.filter(Boolean) : [];
        const validStops = stops.filter((s) =>
            s && hasCoords(s.lat, s.lng, s.location_address),
        );
        const stopTotal = validStops.length;

        if (stopTotal > 0) {
            let dayStopIdx = 0;
            for (const s of validStops) {
                if (!s) continue;
                dayStopIdx += 1;
                stopOrder += 1;
                const hasLatLng = hasCoords(s.lat, s.lng, null);
                const lat = hasLatLng ? Number(s.lat) : null;
                const lng = hasLatLng ? Number(s.lng) : null;
                flat.push({
                    location_type: 'waypoints',
                    location_title: s.location_title || day.title || '',
                    location_description: firstNonEmpty(s.note, day.description, day.location_description),
                    location_address: String(s.location_address || ''),
                    lat: lat != null ? Number(lat) : null,
                    lng: lng != null ? Number(lng) : null,
                    day_id: day.id,
                    day_number: day.dayNumber,
                    order_index: stopOrder,
                    day_stop_index: dayStopIdx,
                    day_stop_total: stopTotal,
                    image: imageUrl,
                });
            }
        } else if (hasCoords(day.lat, day.lng, day.location_address)) {
            stopOrder += 1;
            flat.push({
                location_type: 'waypoints',
                location_title: day.location_title || day.title || '',
                location_description: firstNonEmpty(day.description, day.location_description),
                location_address: day.location_address || '',
                lat: hasCoords(day.lat, day.lng, null) ? Number(day.lat) : null,
                lng: hasCoords(day.lat, day.lng, null) ? Number(day.lng) : null,
                day_id: day.id,
                day_number: day.dayNumber,
                order_index: stopOrder,
                day_stop_index: 1,
                day_stop_total: 1,
                image: imageUrl,
            });
        }
    }

    if (flat.length === 0) return [];

    return flat.map((loc, index) => ({
        ...loc,
        location_type: (index === 0 ? 'origin' : index === flat.length - 1 ? 'destination' : 'waypoints') as
            | 'origin'
            | 'destination'
            | 'waypoints',
    }));
}

export function buildDayFirstMapIndex(locations: TourRouteLocation[]): Map<string, number> {
    const map = new Map<string, number>();
    locations.forEach((loc, index) => {
        if (loc.day_id && !map.has(loc.day_id)) {
            map.set(loc.day_id, index);
        }
    });
    return map;
}
